// components/leads/LeadActivityTimeline.js
// Timeline unificata attività lead - stile Teamleader Focus
// Mostra note, transizioni, appuntamenti e preventivi in ordine cronologico

import React, { useState, useMemo } from 'react';
import { 
  MessageSquare, RefreshCw, Calendar, FileText, 
  Plus, Trash2, Clock, User, ArrowRight, CheckCircle,
  XCircle, AlertCircle, Filter
} from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Separator } from '../ui/separator';
import { formatDateTime, formatDate, formatCurrency, leadStatusLabels } from '../../lib/utils';

// Tipi di eventi per la timeline
const EVENT_TYPES = {
  NOTE: 'note',
  TRANSITION: 'transition',
  APPOINTMENT: 'appointment',
  ESTIMATE: 'estimate',
};

// Configurazione filtri
const FILTERS = [
  { key: 'all', label: 'Tutti', icon: null },
  { key: 'note', label: 'Note', icon: MessageSquare },
  { key: 'transition', label: 'Stati', icon: RefreshCw },
  { key: 'appointment', label: 'Appuntamenti', icon: Calendar },
  { key: 'estimate', label: 'Preventivi', icon: FileText },
];

// Configurazione icone e colori per tipo evento
const EVENT_CONFIG = {
  [EVENT_TYPES.NOTE]: {
    icon: MessageSquare,
    bgColor: 'bg-gray-100',
    iconColor: 'text-gray-600',
    badgeColor: 'bg-gray-200 text-gray-700',
    label: 'Nota',
  },
  [EVENT_TYPES.TRANSITION]: {
    icon: ArrowRight,
    bgColor: 'bg-blue-50',
    iconColor: 'text-blue-600',
    badgeColor: 'bg-blue-100 text-blue-700',
    label: 'Stato',
  },
  [EVENT_TYPES.APPOINTMENT]: {
    icon: Calendar,
    bgColor: 'bg-green-50',
    iconColor: 'text-green-600',
    badgeColor: 'bg-green-100 text-green-700',
    label: 'Appuntamento',
  },
  [EVENT_TYPES.ESTIMATE]: {
    icon: FileText,
    bgColor: 'bg-orange-50',
    iconColor: 'text-[#FF7819]',
    badgeColor: 'bg-orange-100 text-orange-700',
    label: 'Preventivo',
  },
};

// Label stage pipeline (coerenti con LeadPipelineView / LeadDetailPipelinePanel)
const CALL_STAGE_LABELS = {
  contattato: 'Contattato', da_richiamare: 'Da Richiamare', da_assegnare: 'Da Assegnare',
  passato_alla_vendita: 'Passato alla Vendita', non_idoneo: 'Non Idoneo',
};
const SALES_STAGE_LABELS = {
  assegnato: 'Nuovo Lead', appuntamento: 'Appuntamento', sopralluogo: 'Sopralluogo',
  preventivazione: 'Preventivazione', presentato: 'Presentato', trattativa: 'Trattativa',
  trattativa_lunga: 'Trattativa Lunga', contratto: 'Contratto', convertito: 'Convertito', perso: 'Perso',
};

// Mapping stati appuntamento
const APPOINTMENT_STATUS_CONFIG = {
  effettuato: { icon: CheckCircle, color: 'text-green-600', label: 'Effettuato' },
  annullato: { icon: XCircle, color: 'text-red-500', label: 'Annullato' },
  confermato: { icon: CheckCircle, color: 'text-blue-600', label: 'Confermato' },
  da_confermare: { icon: AlertCircle, color: 'text-amber-500', label: 'Da confermare' },
};

/**
 * LeadActivityTimeline - Timeline unificata attività lead
 * @param {Object} props
 * @param {Array} props.notes - Note del lead
 * @param {Array} props.transitions - Transizioni di stato
 * @param {Array} props.appointments - Appuntamenti
 * @param {Array} props.estimates - Preventivi
 * @param {Function} props.onAddNote - Callback per aggiungere nota
 * @param {Function} props.onDeleteNote - Callback per eliminare nota
 * @param {boolean} props.canEdit - Se l'utente può modificare
 * @param {number} props.maxEvents - Numero massimo di eventi da mostrare (undefined = tutti)
 * @param {boolean} props.compact - Se usare layout compatto per il campo nota
 */
const LeadActivityTimeline = ({
  notes = [],
  transitions = [],
  appointments = [],
  estimates = [],
  onAddNote,
  onDeleteNote,
  canEdit = false,
  maxEvents = undefined,
  compact = false,
}) => {
  const [filter, setFilter] = useState('all');
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  // Aggrega tutti gli eventi in un unico array
  const allEvents = useMemo(() => {
    const events = [];

    // Aggiungi note
    if (Array.isArray(notes)) {
      notes.forEach(note => {
        // Skip note che sono già mostrate come parte di transizioni
        const isTransitionNote = note.text?.startsWith('[Transizione');
        events.push({
          id: `note-${note.id}`,
          type: EVENT_TYPES.NOTE,
          date: new Date(note.created_at),
          data: note,
          isTransitionNote,
          deletable: !isTransitionNote, // Le note di transizione non sono eliminabili separatamente
        });
      });
    }

    // Aggiungi transizioni
    if (Array.isArray(transitions)) {
      transitions.forEach(trans => {
        events.push({
          id: `trans-${trans.id || trans.event_id}`,
          type: EVENT_TYPES.TRANSITION,
          date: new Date(trans.created_at),
          data: trans,
          deletable: false,
        });
      });
    }

    // Aggiungi appuntamenti
    if (Array.isArray(appointments)) {
      appointments.forEach(appt => {
        events.push({
          id: `appt-${appt.id}`,
          type: EVENT_TYPES.APPOINTMENT,
          date: new Date(appt.created_at || appt.appointment_date),
          data: appt,
          deletable: false,
        });
      });
    }

    // Aggiungi preventivi
    if (Array.isArray(estimates)) {
      estimates.forEach(est => {
        events.push({
          id: `est-${est.id}`,
          type: EVENT_TYPES.ESTIMATE,
          date: new Date(est.created_at),
          data: est,
          deletable: false,
        });
      });
    }

    // Ordina per data decrescente (più recenti prima)
    return events.sort((a, b) => b.date - a.date);
  }, [notes, transitions, appointments, estimates]);

  // Filtra eventi in base al filtro selezionato
  const filteredEvents = useMemo(() => {
    let events = filter === 'all' ? allEvents : allEvents.filter(event => event.type === filter);
    // Limita il numero di eventi se maxEvents è specificato
    if (maxEvents && events.length > maxEvents) {
      events = events.slice(0, maxEvents);
    }
    return events;
  }, [allEvents, filter, maxEvents]);

  // Handler aggiunta nota
  const handleAddNote = async () => {
    if (!newNote.trim() || !onAddNote) return;
    
    setAddingNote(true);
    try {
      await onAddNote(newNote);
      setNewNote('');
    } finally {
      setAddingNote(false);
    }
  };

  // Render singolo evento
  const renderEvent = (event) => {
    const config = EVENT_CONFIG[event.type];
    const IconComponent = config.icon;

    return (
      <div 
        key={event.id}
        className={`relative pl-8 pb-6 last:pb-0 ${event.isTransitionNote ? 'opacity-60' : ''}`}
        data-testid={`timeline-event-${event.id}`}
      >
        {/* Linea verticale timeline */}
        <div className="absolute left-3 top-0 bottom-0 w-px bg-gray-200 last:hidden" />
        
        {/* Icona evento */}
        <div className={`absolute left-0 w-6 h-6 rounded-full ${config.bgColor} flex items-center justify-center ring-4 ring-white`}>
          <IconComponent className={`w-3 h-3 ${config.iconColor}`} />
        </div>

        {/* Contenuto evento */}
        <div className={`ml-4 p-3 rounded-lg ${config.bgColor} border border-gray-100`}>
          {/* Header evento */}
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="flex items-center flex-wrap gap-2">
              <span className={`text-[10px] sm:text-xs px-2 py-0.5 rounded-full font-medium ${config.badgeColor}`}>
                {config.label}
              </span>
              <span className="text-[10px] sm:text-xs text-gray-500 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatDateTime(event.date)}
              </span>
            </div>
            
            {/* Azione elimina (solo per note eliminabili) */}
            {canEdit && event.deletable && event.type === EVENT_TYPES.NOTE && onDeleteNote && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDeleteNote(event.data.id)}
                className="text-red-500 hover:text-red-600 hover:bg-red-50 h-6 w-6 p-0"
                data-testid={`delete-note-${event.data.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>

          {/* Autore */}
          {renderAuthor(event)}

          {/* Contenuto specifico per tipo */}
          {renderEventContent(event)}
        </div>
      </div>
    );
  };

  // Render autore evento
  const renderAuthor = (event) => {
    let authorName = null;

    switch (event.type) {
      case EVENT_TYPES.NOTE:
        authorName = event.data.created_by_name;
        break;
      case EVENT_TYPES.TRANSITION:
        authorName = event.data.user_name;
        break;
      case EVENT_TYPES.APPOINTMENT:
        authorName = event.data.technician_name || event.data.created_by_name;
        break;
      case EVENT_TYPES.ESTIMATE:
        authorName = event.data.technician_name || event.data.created_by_name;
        break;
      default:
        break;
    }

    if (!authorName) return null;

    return (
      <div className="flex items-center gap-1 text-[10px] sm:text-xs text-gray-500 mb-2">
        <User className="w-3 h-3" />
        <span>{authorName}</span>
      </div>
    );
  };

  // Render contenuto specifico per tipo evento
  const renderEventContent = (event) => {
    switch (event.type) {
      case EVENT_TYPES.NOTE:
        return (
          <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
            {event.data.text}
          </p>
        );

      case EVENT_TYPES.TRANSITION: {
        const d = event.data;
        // Eventi di CREAZIONE (F2.3/F2.4): non esiste uno stato precedente reale.
        if (d.type === 'direct_sales_create') {
          return (
            <div>
              <p className="text-sm text-gray-700 font-medium">Lead creato direttamente in Vendita</p>
              <p className="text-xs text-gray-500 mt-1">
                Stato iniziale:{' '}
                <span className="font-medium text-[#FF7819]">
                  {SALES_STAGE_LABELS[d.to_sales_stage] || 'Nuovo Lead'}
                </span>
              </p>
              {d.technician_name && (
                <p className="text-xs text-gray-500 mt-0.5">Assegnato a: {d.technician_name}</p>
              )}
            </div>
          );
        }
        if (d.type === 'direct_call_create') {
          return (
            <div>
              <p className="text-sm text-gray-700 font-medium">Lead creato</p>
              <p className="text-xs text-gray-500 mt-1">
                Stato iniziale:{' '}
                <span className="font-medium text-[#FF7819]">
                  {CALL_STAGE_LABELS[d.to_call_stage] || 'Contattato'}
                </span>
              </p>
            </div>
          );
        }
        // Follow-up V3 (§14): eventi prossimo passo (impostato/modificato/completato).
        if (d.type === 'next_step_set' || d.type === 'next_step_replaced' || d.type === 'next_step_completed') {
          const m = d.metadata || {};
          const NS_LABELS = {
            ricontattare_cliente: 'Richiamare cliente', completare_revisione_preventivo: 'Completare revisione preventivo',
            fissare_appuntamento: 'Fissare appuntamento', presentare_preventivo: 'Presentare preventivo',
            attendere_cliente: 'Attesa cliente', altro: 'Altro',
          };
          const label = NS_LABELS[m.type] || 'Prossimo passo';
          let due = null;
          try { due = m.due_at ? new Date(m.due_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' }) : null; } catch { /* noop */ }
          const headline = d.type === 'next_step_completed'
            ? 'Prossimo passo completato'
            : d.type === 'next_step_replaced'
              ? `Prossimo passo modificato${m.version ? ` (v${m.version})` : ''}`
              : 'Prossimo passo impostato';
          return (
            <div>
              <p className="text-sm text-gray-700">
                <span className="font-medium">{headline}:</span>{' '}
                <span className="text-[#FF7819] font-medium">{label}</span>
                {due && d.type !== 'next_step_completed' ? <span className="text-gray-500"> · entro {due}</span> : null}
              </p>
              {m.note && m.note.trim() && <p className="text-xs text-gray-500 mt-1 italic">"{m.note}"</p>}
            </div>
          );
        }
        // Determina lo schema: LEGACY (from_status/to_status) vs PIPELINE B1.
        const isLegacy = d.from_status !== undefined || d.to_status !== undefined;
        let fromLabel, toLabel;
        if (isLegacy) {
          fromLabel = leadStatusLabels[d.from_status] || d.from_status || '—';
          toLabel = leadStatusLabels[d.to_status] || d.to_status || '—';
        } else {
          // Schema B1: privilegia il cambio Vendita se presente, altrimenti Chiamata.
          const hasSales = d.to_sales_stage != null || d.from_sales_stage != null;
          if (hasSales) {
            fromLabel = SALES_STAGE_LABELS[d.from_sales_stage] || d.from_sales_stage || '—';
            toLabel = SALES_STAGE_LABELS[d.to_sales_stage] || d.to_sales_stage || '—';
          } else {
            fromLabel = CALL_STAGE_LABELS[d.from_call_stage] || d.from_call_stage || '—';
            toLabel = CALL_STAGE_LABELS[d.to_call_stage] || d.to_call_stage || '—';
          }
        }
        const transNote = d.note || d.notes;
        return (
          <div>
            <p className="text-sm text-gray-700">
              <span className="font-medium">Stato cambiato:</span>{' '}
              <span className="text-gray-500">{fromLabel}</span>
              <ArrowRight className="w-3 h-3 inline mx-1" />
              <span className="font-medium text-[#FF7819]">{toLabel}</span>
            </p>
            {transNote && transNote.trim() && (
              <p className="text-xs text-gray-500 mt-1 italic">
                "{transNote}"
              </p>
            )}
          </div>
        );
      }

      case EVENT_TYPES.APPOINTMENT:
        const apptStatus = APPOINTMENT_STATUS_CONFIG[event.data.status];
        const StatusIcon = apptStatus?.icon || Calendar;
        return (
          <div>
            <p className="text-sm text-gray-700">
              <span className="font-medium capitalize">{event.data.appointment_type || 'Appuntamento'}</span>
              {event.data.appointment_date && (
                <span className="text-gray-500 ml-1">
                  - {formatDate(event.data.appointment_date)}
                  {event.data.appointment_time && ` alle ${event.data.appointment_time}`}
                </span>
              )}
            </p>
            {apptStatus && (
              <div className={`flex items-center gap-1 text-xs mt-1 ${apptStatus.color}`}>
                <StatusIcon className="w-3 h-3" />
                <span>{apptStatus.label}</span>
              </div>
            )}
          </div>
        );

      case EVENT_TYPES.ESTIMATE:
        return (
          <div>
            <p className="text-sm text-gray-700">
              <span className="font-medium">Preventivo {event.data.estimate_number}</span>
              {event.data.total > 0 && (
                <span className="text-[#FF7819] font-bold ml-2">
                  {formatCurrency(event.data.total)}
                </span>
              )}
            </p>
            {event.data.status && (
              <p className="text-xs text-gray-500 mt-1 capitalize">
                {event.data.status.replace(/_/g, ' ')}
              </p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Conta eventi per filtro
  const eventCounts = useMemo(() => ({
    all: allEvents.length,
    note: allEvents.filter(e => e.type === EVENT_TYPES.NOTE).length,
    transition: allEvents.filter(e => e.type === EVENT_TYPES.TRANSITION).length,
    appointment: allEvents.filter(e => e.type === EVENT_TYPES.APPOINTMENT).length,
    estimate: allEvents.filter(e => e.type === EVENT_TYPES.ESTIMATE).length,
  }), [allEvents]);

  return (
    <div className="space-y-4" data-testid="lead-activity-timeline">
      {/* Header con filtri */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-lg font-semibold text-[#3C3C3C] flex items-center gap-2">
          <Clock className="w-5 h-5 text-[#FF7819]" />
          Timeline Attività
        </h3>
        
        {/* Filtri */}
        <div className="flex items-center gap-1 overflow-x-auto pb-1 -mb-1 scrollbar-thin scrollbar-thumb-gray-300">
          {FILTERS.map(f => {
            const count = eventCounts[f.key];
            const isActive = filter === f.key;
            const FilterIcon = f.icon;
            
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`
                  flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium
                  transition-all whitespace-nowrap
                  ${isActive 
                    ? 'bg-[#FF7819] text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }
                `}
                data-testid={`filter-${f.key}`}
              >
                {FilterIcon && <FilterIcon className="w-3 h-3" />}
                <span>{f.label}</span>
                <span className={`
                  ml-1 px-1.5 py-0.5 rounded-full text-[10px]
                  ${isActive ? 'bg-white/20' : 'bg-gray-200'}
                `}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <Separator />

      {/* Input nuova nota - compatto se compact=true */}
      {canEdit && onAddNote && (
        <div className={`flex gap-2 ${compact ? 'items-center' : ''}`}>
          <Textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder={compact ? "Aggiungi una nota..." : "Aggiungi una nota (es: chiamato, non risponde, appuntamento confermato...)"}
            className={`flex-1 text-sm resize-none ${compact ? 'min-h-[44px] py-2.5' : 'min-h-[70px]'}`}
            rows={compact ? 1 : 2}
            data-testid="timeline-new-note-input"
          />
          <Button
            onClick={handleAddNote}
            disabled={addingNote || !newNote.trim()}
            className={`btn-primary ${compact ? 'h-[44px] w-[44px]' : 'self-end h-10 w-10'} p-0`}
            data-testid="timeline-add-note-btn"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Timeline eventi */}
      <div className="relative">
        {filteredEvents.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Clock className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">
              {filter === 'all' 
                ? 'Nessuna attività registrata' 
                : `Nessun evento di tipo "${FILTERS.find(f => f.key === filter)?.label}"`
              }
            </p>
          </div>
        ) : (
          <div className="space-y-0">
            {filteredEvents.map(event => renderEvent(event))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadActivityTimeline;
